import React from 'react'
import {
  Box,
  Flex,
  Heading,
  Image,
  Link,
  useColorModeValue,
} from '@chakra-ui/react'
import ReactMarkDown from 'react-markdown'
import { ArrowForwardIcon } from '@chakra-ui/icons'

type CardImage = {
  url: string
  alternativeText?: string | null
}

export type CardData = {
  title: string
  description: string
  iconImage: CardImage
  iconImageMobile?: CardImage
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down'
  objectPosition?: string
  url?: string
  urlLabel?: string
}

type Props = {
  title: string
  subtitle?: string
  alignStart?: boolean
  columns?: number
  cardImageOverlay?: boolean
  cardImageBg?: string
  cardImageHeight?: number | string
  cardContentPy?: number | string
  cardBg?: string
  cardBorderColor?: string
  cardTitleColor?: string
  cardDescriptionColor?: string
  link?: {
    url: string
    text: string
    newTab: boolean
  }
  cards?: CardData[]
}

export const SectionWithCards = ({
  title,
  subtitle,
  alignStart,
  columns,
  cardImageOverlay = true,
  cardImageBg,
  cardImageHeight,
  cardContentPy,
  cardBg = 'black',
  cardBorderColor = 'whiteAlpha.500',
  cardTitleColor = 'white',
  cardDescriptionColor = 'whiteAlpha.800',
  link,
  cards,
}: Props) => {
  const secondaryColor = useColorModeValue('blackAlpha.700', 'whiteAlpha.700')
  const textAlign = alignStart ? 'start' : 'center'
  return (
    <>
      <Heading
        variant="spaceGrotesk"
        as="h2"
        fontSize="40px"
        textAlign={textAlign}
        fontWeight={300}
      >
        {title}
      </Heading>
      {subtitle && (
        <Heading
          variant="spaceGrotesk"
          as="h2"
          fontSize="24px"
          textAlign={textAlign}
          fontWeight={300}
          mt={2}
          color={secondaryColor}
        >
          {subtitle}
        </Heading>
      )}
      {cards?.length ? (
        <Flex gap={9} my={9} wrap="wrap">
          {cards.map((card: CardData, index: number) => (
            <Box
              key={`${index}-${card.title}`}
              flexBasis={{
                base: '100%',
                md: columns
                  ? `calc(${100 / columns}% - var(--chakra-space-9))`
                  : 'auto',
              }}
              flex={columns ? 'auto' : '1'}
              minWidth={0}
              bg={cardBg}
              rounded="2xl"
              border="1px"
              borderColor={cardBorderColor}
              overflow="hidden"
              borderRadius="22px"
            >
              <Box
                position="relative"
                bg={cardImageBg}
                height={cardImageHeight}
                overflow="hidden"
              >
                {card.iconImageMobile && (
                  <Image
                    display={{ base: 'block', md: 'none' }}
                    src={card.iconImageMobile.url}
                    alt={card.iconImageMobile.alternativeText || card.title}
                    width="100%"
                    height={cardImageHeight ? '100%' : undefined}
                    objectFit={card.objectFit ?? 'cover'}
                    mb={cardImageHeight ? 0 : 2}
                  />
                )}
                <Image
                  display={
                    card.iconImageMobile
                      ? { base: 'none', md: 'block' }
                      : 'block'
                  }
                  src={card.iconImage.url}
                  alt={card.iconImage.alternativeText || card.title}
                  width="100%"
                  height={cardImageHeight ? '100%' : undefined}
                  objectFit={card.objectFit ?? 'cover'}
                  objectPosition={card.objectPosition}
                  mb={cardImageHeight ? 0 : 2}
                />
                {cardImageOverlay && (
                  <Box
                    position="absolute"
                    bottom={0}
                    width="full"
                    height="87px"
                    background={
                      index !== 1
                        ? 'linear-gradient(180deg, #FFF 0%, #000 100%)'
                        : ''
                    }
                    mixBlendMode="multiply"
                  ></Box>
                )}
              </Box>
              <Box px={8} py={cardContentPy}>
                <Heading
                  as="h4"
                  mb={4}
                  fontSize="24px"
                  lineHeight={1.5}
                  color={cardTitleColor}
                >
                  {card.title}
                </Heading>
                <Box
                  mb="16px"
                  fontWeight={500}
                  lineHeight={1.375}
                  color={cardDescriptionColor}
                  opacity={0.7}
                >
                  <ReactMarkDown>{card.description}</ReactMarkDown>
                </Box>
                <Link
                  href={card.url}
                  isExternal
                  color="blue.250"
                  display={card.url ? 'block' : 'none'}
                >
                  {card.urlLabel}
                  <ArrowForwardIcon />
                </Link>
              </Box>
            </Box>
          ))}
        </Flex>
      ) : null}
      {link && (
        <Box textAlign={'center'}>
          <Link
            href={link.url}
            isExternal={link.newTab}
            variant="black"
            textDecoration={'underline'}
          >
            {link.text}
          </Link>
        </Box>
      )}
    </>
  )
}
